import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { HistoryFacade } from '../history-entries';
import { WorkspaceFacade } from '../workspace';
import { ChatMessage, RepoOverviewState, ReposSettings, ReposSettingsPatch } from './repos.models';
import { ReposActions } from './repos.actions';
import {
  selectHasGitHubInstallation,
  selectReposChatMessages,
  selectReposError,
  selectReposLoading,
  selectReposProjectDefinition,
  selectReposSettings,
  selectReposState,
} from './repos.selectors';

@Injectable({ providedIn: 'root' })
export class ReposFacade {
  private store = inject(Store);
  private workspace = inject(WorkspaceFacade);
  private history = inject(HistoryFacade);

  // Selectors always yield a shaped value synchronously, so requireSync gives
  // non-null signals (matching the legacy store's always-shaped settings/state).
  readonly settings = toSignal(this.store.select(selectReposSettings), { requireSync: true });
  readonly state = toSignal(this.store.select(selectReposState), { requireSync: true });
  readonly chatMessages = toSignal(this.store.select(selectReposChatMessages), { requireSync: true });
  readonly projectDefinition = toSignal(this.store.select(selectReposProjectDefinition), { requireSync: true });
  readonly hasGitHubInstallation = toSignal(this.store.select(selectHasGitHubInstallation), { requireSync: true });
  readonly loading = toSignal(this.store.select(selectReposLoading), { requireSync: true });
  readonly error = toSignal(this.store.select(selectReposError), { requireSync: true });
  readonly activeProjectId = this.workspace.activeProjectId;

  load(): void {
    this.store.dispatch(ReposActions.load());
  }
  setChatMessages(messages: ChatMessage[], projectId?: string): void {
    this.store.dispatch(ReposActions.setChatMessages({ messages, projectId }));
  }
  saveSettings(settings: ReposSettings, projectId?: string): void {
    this.store.dispatch(ReposActions.saveSettings({ settings, projectId }));
  }
  patchSettings(patch: ReposSettingsPatch, projectId?: string): void {
    this.store.dispatch(ReposActions.patchSettings({ patch, projectId }));
  }
  saveState(state: RepoOverviewState, projectId?: string): void {
    this.store.dispatch(ReposActions.saveState({ state, projectId }));
  }
  patchState(patch: Partial<RepoOverviewState>, projectId?: string): void {
    this.store.dispatch(ReposActions.patchState({ patch, projectId }));
  }
  async logChatEvent(
    text: string,
    kind: 'user' | 'assistant' | 'info' | 'update' | 'error' = 'info',
    projectIdOverride?: string
  ): Promise<void> {
    const projectId = projectIdOverride ?? this.activeProjectId();
    if (!projectId) return;
    await this.history.add(projectId, 'repos.chat', text, kind as any);
  }
}
